import { Navigate, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import type { ReactNode } from "react";
import AppShell from "./layouts/AppShell";
import SignIn from "./pages/SignIn";
import AcceptInvite from "./pages/AcceptInvite";
import { ReferralLanding } from "./pages/ReferralLanding";
import Landing from "./pages/Landing";
import { useAuth } from "./lib/auth";
import { useFreshBuild } from "./lib/freshness";
import { homeFor } from "./lib/h1bProduct";
import { saveNextLocation } from "./lib/nextLocation";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { OperatorBar } from "./components/OperatorBar";

// Code-split the heavy interactive pages. ScholarPath, EvidenceCriteria,
// LetterStudio, and I140Intake are each ~1k–3k lines of @ts-nocheck
// React with their own inlined design CSS. Eagerly importing all of
// them inflated the first paint of /sign-in and the marketing landing
// with code those screens never use. lazy() puts each behind its own
// chunk that only loads when the user navigates to it.
const Applications = lazy(() => import("./pages/Applications"));
const AdminToday = lazy(() => import("./pages/admin/Today"));
const AdminCases = lazy(() => import("./pages/admin/Cases"));
const AdminClients = lazy(() => import("./pages/admin/Cases").then((m) => ({ default: m.Clients })));
const AdminStatements = lazy(() => import("./pages/admin/Statements"));
const AdminLeads = lazy(() => import("./pages/admin/Leads"));
const AdminCase = lazy(() => import("./pages/admin/CasePage"));
const ApplicationDetail = lazy(() => import("./pages/ApplicationDetail"));
const Profile = lazy(() => import("./pages/Profile"));
const Engagements = lazy(() => import("./pages/Engagements"));
const EngagementWorkspacePage = lazy(() => import("./pages/EngagementWorkspace"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const Publications = lazy(() => import("./pages/Publications"));
const Recommenders = lazy(() => import("./pages/Recommenders"));
const ScholarImport = lazy(() => import("./pages/ScholarImport"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const ScholarPathGuided = lazy(() => import("./pages/ScholarPath"));
const I140Intake = lazy(() => import("./pages/I140Intake"));
const LetterStudio = lazy(() => import("./pages/LetterStudio"));
const DocumentPortal = lazy(() => import("./pages/DocumentPortal"));
const LetterUpload = lazy(() => import("./pages/LetterUpload"));
const ReviewBeforeCheckout = lazy(() => import("./pages/ReviewBeforeCheckout"));
const PostCheckout = lazy(() => import("./pages/PostCheckout"));
const Refer = lazy(() => import("./pages/Refer"));
const SharedView = lazy(() => import("./pages/SharedView"));
const Expert = lazy(() => import("./pages/Expert"));
const Assessment = lazy(() => import("./pages/Assessment"));
const Welcome = lazy(() => import("./pages/Welcome"));
const PetitionBrief = lazy(() => import("./pages/PetitionBrief"));
const PetitionPackage = lazy(() => import("./pages/PetitionPackage"));
const RfeResponse = lazy(() => import("./pages/RfeResponse"));
const AttorneyReview = lazy(() => import("./pages/AttorneyReview"));
const AttorneyPortal = lazy(() => import("./pages/AttorneyPortal"));
const EvidenceCriteria = lazy(() => import("./pages/EvidenceCriteria"));
// H-1B product (employer seat + the worker's public portal).
const H1BHub = lazy(() => import("./pages/h1b/Hub"));
const H1BEmployer = lazy(() => import("./pages/h1b/Employer"));
const H1BPosition = lazy(() => import("./pages/h1b/Position"));
const H1BPetition = lazy(() => import("./pages/h1b/Petition"));
const H1BDocuments = lazy(() => import("./pages/h1b/Documents"));
const H1BInvite = lazy(() => import("./pages/h1b/Invite"));
const H1BSupportLetter = lazy(() => import("./pages/h1b/SupportLetter"));
const H1BPacket = lazy(() => import("./pages/h1b/Packet"));
const H1BWorkerPortal = lazy(() => import("./pages/h1b/WorkerPortal"));
// Founder O-1A product.
const O1AHome = lazy(() => import("./pages/o1a/Home"));
const O1AStart = lazy(() => import("./pages/o1a/Start"));
const O1ATask = lazy(() => import("./pages/o1a/Task"));
const O1APeople = lazy(() => import("./pages/o1a/People"));
const O1ACompany = lazy(() => import("./pages/o1a/Company"));
const O1ACompanyPrint = lazy(() => import("./pages/o1a/Company").then((m) => ({ default: m.O1ACompanyPrint })));
const O1AReview = lazy(() => import("./pages/o1a/Review"));
const O1APortal = lazy(() => import("./pages/o1a/Portal"));
const AdminFounders = lazy(() => import("./pages/admin/Founders"));

// Lightweight loading fallback — matches the warm palette so the user
// doesn't see a flash of white between routes.
function RouteLoading() {
  return (
    <div
      className="min-h-screen bg-soft flex items-center justify-center text-small text-ink-2"
      role="status"
      aria-live="polite"
    >
      Loading…
    </div>
  );
}

function StartRedirect() {
  const [params] = useSearchParams();
  const keep = new URLSearchParams();
  for (const key of ["scholar", "plan"]) {
    const v = params.get(key);
    if (v) keep.set(key, v);
  }
  const qs = keep.toString();
  return <Navigate to={qs ? `/assessment?${qs}` : "/assessment"} replace />;
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const location = useLocation();
  // Remember the full intended URL (pathname + search) so SignIn can
  // return the user there after the code is verified. A bare redirect
  // used to drop `?scholar=…` pasted on the marketing landing page.
  // Done in an effect (not during render) so StrictMode double-renders
  // and re-renders don't write it repeatedly; signOut clears it.
  const authed = !!session;
  useEffect(() => {
    if (!authed) saveNextLocation(location.pathname, location.search);
  }, [authed, location.pathname, location.search]);
  if (!session) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to="/sign-in" replace state={{ next }} />;
  }
  return <>{children}</>;
}

function RedirectIfSignedIn({
  children,
  to,
}: {
  children: ReactNode;
  to: string;
}) {
  const { session } = useAuth();
  if (session) return <Navigate to={to} replace />;
  return <>{children}</>;
}

export default function App() {
  useFreshBuild();
  return (
    <ErrorBoundary>
    <OperatorBar />
    <Suspense fallback={<RouteLoading />}>
    <Routes>
      {/* Public marketing landing — design handoff's front door. Signed-in
          users get bounced into the app at /scholar-path. */}
      <Route
        path="/"
        element={
          <RedirectIfSignedIn to={homeFor()}>
            <Landing />
          </RedirectIfSignedIn>
        }
      />
      <Route
        path="/sign-in"
        element={
          <RedirectIfSignedIn to={homeFor()}>
            <SignIn />
          </RedirectIfSignedIn>
        }
      />
      {/* H-1B product. The worker's portal is public: the signed link in the
          URL is the credential (see app/api/v1/h1b.py). */}
      <Route path="/h1b/portal/:token" element={<H1BWorkerPortal />} />
      <Route path="/h1b" element={<ProtectedRoute><H1BHub /></ProtectedRoute>} />
      <Route path="/h1b/employer" element={<ProtectedRoute><H1BEmployer /></ProtectedRoute>} />
      <Route path="/h1b/position" element={<ProtectedRoute><H1BPosition /></ProtectedRoute>} />
      <Route path="/h1b/petition" element={<ProtectedRoute><H1BPetition /></ProtectedRoute>} />
      <Route path="/h1b/documents" element={<ProtectedRoute><H1BDocuments /></ProtectedRoute>} />
      <Route path="/h1b/invite" element={<ProtectedRoute><H1BInvite /></ProtectedRoute>} />
      <Route path="/h1b/support-letter" element={<ProtectedRoute><H1BSupportLetter /></ProtectedRoute>} />
      <Route path="/h1b/packet" element={<ProtectedRoute><H1BPacket /></ProtectedRoute>} />
      {/* Founder O-1A product: the founder plan and its stages. */}
      <Route path="/o1a" element={<ProtectedRoute><O1AHome /></ProtectedRoute>} />
      <Route path="/o1a/start" element={<ProtectedRoute><O1AStart /></ProtectedRoute>} />
      <Route path="/o1a/start/:step" element={<ProtectedRoute><O1AStart /></ProtectedRoute>} />
      <Route path="/o1a/task/:taskId" element={<ProtectedRoute><O1ATask /></ProtectedRoute>} />
      <Route path="/o1a/people" element={<ProtectedRoute><O1APeople /></ProtectedRoute>} />
      <Route path="/o1a/company" element={<ProtectedRoute><O1ACompany /></ProtectedRoute>} />
      <Route path="/o1a/company/print" element={<ProtectedRoute><O1ACompanyPrint /></ProtectedRoute>} />
      <Route path="/o1a/review" element={<ProtectedRoute><O1AReview /></ProtectedRoute>} />
      {/* The company signer's link: public, the token is the credential. */}
      <Route path="/c/:token" element={<O1APortal />} />
      {/* The first founder pages, before the task flow. */}
      <Route path="/o1a/path" element={<Navigate to="/o1a/start/confirm" replace />} />
      <Route path="/o1a/plan" element={<Navigate to="/o1a" replace />} />
      <Route path="/o1a/evidence" element={<Navigate to="/o1a" replace />} />
      <Route path="/o1a/letters" element={<Navigate to="/o1a/people" replace />} />
      {/* Marketing landing CTAs ("Check eligibility", pricing cards) link to
          app.visagenius.io/start. It opens the Case assessment, keeping
          ?scholar= (the link pasted on the home page, so it isn't asked for
          again) and ?plan= (the pricing card they clicked). */}
      <Route path="/start" element={<StartRedirect />} />
      {/* Public: the result shows before we ask for an email. */}
      <Route path="/assessment" element={<Assessment />} />
      <Route path="/auth/accept-invite" element={<AcceptInvite />} />
      {/* Public, unauthenticated — referral link landing. Stashes the
          referrer's tenant slug in localStorage and forwards to the
          regular onboarding entry. SignIn reads the slug on signup
          and passes it to the backend as referral_code. */}
      <Route path="/r/:slug" element={<ReferralLanding />} />
      {/* Public, unauthenticated — recommender magic-link landing page. */}
      <Route path="/letter-upload/:token" element={<LetterUpload />} />
      {/* Public, unauthenticated — attorney share-link landing page. */}
      <Route path="/share/:token" element={<SharedView />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />

      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/app" element={<Navigate to="/scholar-path" replace />} />
        <Route path="/applications" element={<Applications />} />
        <Route path="/applications/:applicationId" element={<ApplicationDetail />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/intake" element={<ScholarImport />} />
        <Route path="/publications" element={<Publications />} />
        <Route path="/recommenders" element={<Recommenders />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/engagements" element={<Engagements />} />
        <Route
          path="/engagements/:engagementId"
          element={<EngagementWorkspacePage />}
        />
      </Route>

      {/* Scholar Path (Guided) — the card-based conversion flow. Sign-in is
          the front door of the product, so this route is auth-gated. The
          SignIn.verifyCode handler routes new users (no applications yet)
          to step 1 (EligibilityScholar — Google Scholar URL prompt) and
          returning users straight to Case Plan (step 5 or 6). Letter
          Studio stays public so the Case Plan tile ("Recommendation
          letters") still works as a guest preview, and the marketing
          Landing page at "/" remains public. */}
      {/* A price button on the website lands here (?plan=eb1a); signed-out
          visitors sign up first, with the plan carried through. */}
      <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
      <Route
        path="/scholar-path"
        element={
          <ProtectedRoute>
            <ScholarPathGuided />
          </ProtectedRoute>
        }
      />

      <Route
        path="/i140-intake"
        element={
          <ProtectedRoute>
            <I140Intake />
          </ProtectedRoute>
        }
      />

      <Route
        path="/letter-studio"
        element={<LetterStudio />}
      />

      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <DocumentPortal />
          </ProtectedRoute>
        }
      />

      <Route
        path="/review/:applicationId"
        element={
          <ProtectedRoute>
            <ReviewBeforeCheckout />
          </ProtectedRoute>
        }
      />

      <Route
        path="/post-checkout"
        element={
          <ProtectedRoute>
            <PostCheckout />
          </ProtectedRoute>
        }
      />

      <Route
        path="/refer"
        element={
          <ProtectedRoute>
            <Refer />
          </ProtectedRoute>
        }
      />

      <Route path="/expert" element={<Expert />} />

      <Route
        path="/petition-brief"
        element={
          <ProtectedRoute>
            <PetitionBrief />
          </ProtectedRoute>
        }
      />

      <Route
        path="/petition-package"
        element={
          <ProtectedRoute>
            <PetitionPackage />
          </ProtectedRoute>
        }
      />

      {/* RFE / NOID response for a filed case (upload the notice, triage,
          evidence plan, draft, index, package). */}
      <Route
        path="/rfe-response"
        element={
          <ProtectedRoute>
            <RfeResponse />
          </ProtectedRoute>
        }
      />

      <Route path="/attorney-review" element={<Navigate to="/review-and-file" replace />} />
      <Route path="/admin" element={<ProtectedRoute><AdminToday /></ProtectedRoute>} />
      <Route path="/admin/cases" element={<ProtectedRoute><AdminCases /></ProtectedRoute>} />
      <Route path="/admin/cases/:tenantId" element={<ProtectedRoute><AdminCase /></ProtectedRoute>} />
      <Route path="/admin/leads" element={<ProtectedRoute><AdminLeads /></ProtectedRoute>} />
      <Route path="/admin/billing" element={<ProtectedRoute><AdminStatements /></ProtectedRoute>} />
      <Route path="/admin/statements" element={<ProtectedRoute><AdminStatements /></ProtectedRoute>} />
      <Route path="/admin/clients" element={<ProtectedRoute><AdminClients /></ProtectedRoute>} />
      <Route path="/admin/founders" element={<ProtectedRoute><AdminFounders /></ProtectedRoute>} />
      <Route path="/admin/founders/:caseId" element={<ProtectedRoute><AdminFounders /></ProtectedRoute>} />
      <Route
        path="/review-and-file"
        element={
          <ProtectedRoute>
            <AttorneyReview />
          </ProtectedRoute>
        }
      />

      <Route
        path="/attorney-portal"
        element={
          <ProtectedRoute>
            <AttorneyPortal />
          </ProtectedRoute>
        }
      />

      {/* Signed-in only, like /scholar-path: the page reads and saves the
          case through the API, and signed out it showed a made-up "we
          checked your Scholar profile and found 0" read that saved nothing. */}
      <Route path="/evidence" element={<ProtectedRoute><EvidenceCriteria /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}
